import { useState } from 'react'
import { Box, Collapse } from '@mui/material'

import { Tooltip } from '@components/shared/Tooltip'
import { getMenuLabel } from '../../helpers/getMenuLabel'
import { getSideBarIcon } from '../../helpers/getSideBarIcon'
import { showAnimation } from '../../helpers/animations'
import { ArrowDownDate } from '@assets/svgs/ArrowDownDate'
import { ArrowUpDate } from '@assets/svgs/ArrowUpDate'
import { InfoIcon } from '@assets/svgs/InfoIcon'
import { useUser } from '@hooks/useUser'

import type { MenuItensProps, SubMenuItemProps } from './types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'

import * as S from './styles'

const findPermissionById = (items: PermissionsItemProps[], id: number): PermissionsItemProps | null => {
  for (const item of items) {
    if (item.id === id) return item
    if (item.children?.length) {
      const found = findPermissionById(item.children, id)
      if (found) return found
    }
  }
  return null
}

const SubMenuItem = ({
  item,
  basePath,
  depth,
  sideBarCollapsed,
  pathname,
  navigate,
  hasMounted,
  hasViewPermission
}: SubMenuItemProps) => {
  const [isOpen, setIsOpen] = useState(true)

  const itemPath = basePath + item.path
  const itemLabel = getMenuLabel(item.id, item.name)
  const visibleChildren = item.children?.filter((child) => hasViewPermission(child.id)) ?? []

  if (!hasViewPermission(item.id) && !visibleChildren.length) return null

  return (
    <Box>
      <Tooltip
        title={
          !item.isActive ? (
            <S.InfoIconBox>
              <InfoIcon /> Em breve! Categoria em desenvolvimento
            </S.InfoIconBox>
          ) : !sideBarCollapsed ? (
            itemLabel
          ) : (
            ''
          )
        }
        placement="right"
      >
        <S.SubMenuBox
          isOpen={sideBarCollapsed}
          isSelected={!!(pathname === itemPath)}
          depth={depth}
          onClick={() => {
            if (!!item.path) navigate(itemPath)
          }}
        >
          {!sideBarCollapsed && (
            <S.Icon isSelected={!!(pathname === itemPath)} isOpen={sideBarCollapsed}>
              {getSideBarIcon(item.icon)}
            </S.Icon>
          )}

          {sideBarCollapsed && (
            <S.MenuText variants={showAnimation} initial={hasMounted ? 'hidden' : false} animate="show" exit="hidden">
              {itemLabel}
            </S.MenuText>
          )}

          {!!visibleChildren.length && (
            <S.ArrowIconBox
              onClick={(event) => {
                event.stopPropagation()
                setIsOpen(!isOpen)
              }}
            >
              {!isOpen ? <ArrowDownDate /> : <ArrowUpDate />}
            </S.ArrowIconBox>
          )}
        </S.SubMenuBox>
      </Tooltip>

      {!!visibleChildren.length && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <S.SubMenusBox>
            {visibleChildren.map((child) => (
              <SubMenuItem
                key={`submenu-${child.id}`}
                item={child}
                basePath={itemPath}
                depth={depth + 1}
                sideBarCollapsed={sideBarCollapsed}
                pathname={pathname}
                navigate={navigate}
                hasMounted={hasMounted}
                hasViewPermission={hasViewPermission}
              />
            ))}
          </S.SubMenusBox>
        </Collapse>
      )}
    </Box>
  )
}

export const MenuItens = ({ parent, sideBarCollapsed, pathname, navigate, hasMounted }: MenuItensProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const { userPermissionsData } = useUser()

  const hasViewPermission = (itemId: number): boolean => {
    if (!userPermissionsData?.permissions?.length) return true

    const permission = findPermissionById(userPermissionsData.permissions, itemId)
    if (!permission) return true

    return permission.totalPermission || permission.canView
  }

  const parentLabel = getMenuLabel(parent.id, parent.name)
  const visibleChildren = parent.children?.filter((child) => hasViewPermission(child.id)) ?? []

  if (!hasViewPermission(parent.id) && !visibleChildren.length) return null

  return (
    <Box key={`menu-${parent.id}`}>
      <Tooltip
        title={
          !parent.isActive ? (
            <S.InfoIconBox>
              <InfoIcon /> Em breve! Categoria em desenvolvimento
            </S.InfoIconBox>
          ) : !sideBarCollapsed ? (
            parentLabel
          ) : (
            ''
          )
        }
        placement="right"
      >
        <S.MenuBox
          isSelected={!!(pathname === parent.path)}
          isOpen={sideBarCollapsed}
          onClick={() => {
            !!visibleChildren.length && setIsOpen(!isOpen)

            if (!!parent.path && !visibleChildren.length) navigate(parent.path)
          }}
        >
          <S.Icon isSelected={!!(pathname === parent.path)} isOpen={sideBarCollapsed}>
            {!!parent.icon && getSideBarIcon(parent.icon)}
          </S.Icon>

          {sideBarCollapsed && (
            <S.MenuText variants={showAnimation} initial={hasMounted ? 'hidden' : false} animate="show" exit="hidden">
              {parentLabel}
            </S.MenuText>
          )}

          {!!visibleChildren.length &&
            (!isOpen ? (
              <S.ArrowIconBox>
                <ArrowDownDate />
              </S.ArrowIconBox>
            ) : (
              <S.ArrowIconBox>
                <ArrowUpDate />
              </S.ArrowIconBox>
            ))}
        </S.MenuBox>
      </Tooltip>

      {!!visibleChildren.length && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <S.SubMenusBox>
            {visibleChildren.map((children) => (
              <SubMenuItem
                key={`submenu-${children.id}`}
                item={children}
                basePath={parent.path}
                depth={0}
                sideBarCollapsed={sideBarCollapsed}
                pathname={pathname}
                navigate={navigate}
                hasMounted={hasMounted}
                hasViewPermission={hasViewPermission}
              />
            ))}
          </S.SubMenusBox>
        </Collapse>
      )}
    </Box>
  )
}
